import { Label, Text, Stack } from '@figma/fpl-components';

import * as stylex from '@stylexjs/stylex';
import { useCallback, useState, type ReactNode } from 'react';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useIsHovered } from '@/hooks/useIsHovered';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { FlexCenVer, FlexCol } from '../styles';
import { EllipsisText } from '../EllipsisText/EllipsisText';
import { AppInfoIcon } from '../AppInfoIcon/AppInfoIcon';

export interface PanelFieldProps {
  label?: string;
  /** Associates the visible label with one form control in the FPL implementation. */
  labelFor?: string;
  tooltipText?: string;
  slot?: ReactNode;
  showSlotOnHoverOnly?: boolean;
  slotContainerH?: number;
  disabled?: boolean;
  useHorizontalLayout?: boolean;
  children: ReactNode;
  labelGap?: number;
  controlGap?: number;
}

interface PanelFieldImplementationProps extends PanelFieldProps {
  showSlotOnHoverOnly: boolean;
  slotContainerH: number;
  useHorizontalLayout: boolean;
  disabled: boolean;
}

const LEGACY_SPACING_UNIT_PX = 8;
const CONTROL_GAP_PX = 4;
const HORIZONTAL_GAP_PX = 8;

const styles = stylex.create({
  root: {
    display: 'flex',
    minWidth: 0,
  },
  rootVertical: {
    flexDirection: 'column',
  },
  rootHorizontal: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  content: {
    minWidth: 0,
  },
  header: {
    minWidth: 0,
  },
  labelContainer: {
    minWidth: 0,
  },
  control: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    display: 'flex',
    minWidth: 0,
  },
  labelText: {
    minWidth: 0,
  },
});

function LegacyPanelField({
  label,
  tooltipText,
  slot,
  useHorizontalLayout = false,
  showSlotOnHoverOnly = false,
  slotContainerH = 24,
  disabled = false,
  children,
  labelGap = 0.75,
}: PanelFieldImplementationProps) {
  const { isHovered, ...elementProps } = useIsHovered();

  const labelElement = (
    <EllipsisText
      variant="body-sm-rg"
      sx={{
        color: disabled ? color.White16_T : color.White64_T,
      }}
    >
      {label}
    </EllipsisText>
  );

  const tooltipElement = tooltipText && (
    <FlexCenVer
      sx={{
        color: disabled ? color.White16_T : color.White64_T,
        '&:hover': { color: disabled ? color.White16_T : color.White80_T },
      }}
    >
      <AppInfoIcon tooltipProps={{ title: tooltipText }} size={16} />
    </FlexCenVer>
  );

  const slotElement = showSlotOnHoverOnly ? isHovered && slot : slot;

  if (useHorizontalLayout) {
    return (
      <FlexCenVer sx={{ gap: 1 }} {...elementProps}>
        <FlexCenVer sx={{ gap: 0.5 }}>{children}</FlexCenVer>
        <FlexCenVer sx={{ gap: 0.5, height: slotContainerH, flex: 1, minWidth: 0, justifyContent: 'space-between' }}>
          <FlexCenVer sx={{ gap: 0.5, minWidth: 0 }}>
            {labelElement}
            {tooltipElement}
          </FlexCenVer>
          {slotElement}
        </FlexCenVer>
      </FlexCenVer>
    );
  }

  return (
    <FlexCol sx={{ gap: labelGap }} {...elementProps}>
      {label && (
        <>
          <FlexCenVer sx={{ justifyContent: 'space-between', height: slotContainerH }}>
            <FlexCenVer sx={{ gap: 0.5, minWidth: 0 }}>
              {labelElement}
              {tooltipElement}
            </FlexCenVer>
            {slotElement}
          </FlexCenVer>
        </>
      )}
      <FlexCenVer sx={{ gap: 0.5 }}>{children}</FlexCenVer>
    </FlexCol>
  );
}

function FplPanelFieldLabel({ disabled, label, labelFor }: Pick<PanelFieldProps, 'disabled' | 'label' | 'labelFor'>) {
  const [isTruncated, setIsTruncated] = useState(false);
  const handleTruncationChange = useCallback((nextIsTruncated: boolean) => {
    setIsTruncated(nextIsTruncated);
  }, []);

  if (!label) return null;

  const labelText = (
    <Text.span
      {...stylex.props(styles.labelText)}
      color={disabled ? 'disabled' : 'secondary'}
      hideTooltip
      onTruncationChange={handleTruncationChange}
      truncate
      variant="body-small"
    >
      {label}
    </Text.span>
  );

  const labelElement = labelFor ? (
    <Label {...stylex.props(styles.label)} htmlFor={labelFor} variant="secondary">
      {labelText}
    </Label>
  ) : (
    labelText
  );

  return <Tooltip title={isTruncated ? label : undefined}>{labelElement}</Tooltip>;
}

function FplPanelField({
  children,
  controlGap = CONTROL_GAP_PX,
  disabled,
  label,
  labelFor,
  labelGap = 0.5,
  showSlotOnHoverOnly,
  slot,
  slotContainerH,
  tooltipText,
  useHorizontalLayout,
}: PanelFieldImplementationProps) {
  const { isHovered, ...elementProps } = useIsHovered();
  const rootProps = stylex.props(styles.root, useHorizontalLayout ? styles.rootHorizontal : styles.rootVertical);
  const labelElement = (
    <Stack.Row {...stylex.props(styles.labelContainer)}>
      <FplPanelFieldLabel disabled={disabled} label={label} labelFor={labelFor} />
    </Stack.Row>
  );
  const slotElement = slot ? (
    <div
      style={{
        flexShrink: 0,
        visibility: showSlotOnHoverOnly && !isHovered ? 'hidden' : 'visible',
      }}
    >
      {slot}
    </div>
  ) : null;
  const controlElement = useHorizontalLayout ? (
    <Stack.Row gap={controlGap}>{children}</Stack.Row>
  ) : (
    <Stack.Row {...stylex.props(styles.control)} gap={controlGap}>
      {children}
    </Stack.Row>
  );
  const controlWithTooltip = tooltipText ? <Tooltip title={tooltipText}>{controlElement}</Tooltip> : controlElement;

  if (useHorizontalLayout) {
    const inputRow = (
      <div
        {...rootProps}
        {...elementProps}
        style={{
          ...rootProps.style,
          gap: CONTROL_GAP_PX,
          minHeight: slotContainerH,
        }}
      >
        {controlWithTooltip}
        {labelElement}
        <Stack.Spacer />
        {slotElement}
      </div>
    );

    return inputRow;
  }

  const inputRow = (
    <Stack.Row {...stylex.props(styles.content)} align="start" gap={HORIZONTAL_GAP_PX}>
      {controlWithTooltip}
      {slotElement}
    </Stack.Row>
  );

  return (
    <div
      {...rootProps}
      {...elementProps}
      style={{
        ...rootProps.style,
        gap: labelGap * LEGACY_SPACING_UNIT_PX,
      }}
    >
      {label ? <Stack.Row {...stylex.props(styles.header)}>{labelElement}</Stack.Row> : null}
      {inputRow}
    </div>
  );
}

/**
 * Standardized label, help, control, and trailing-slot layout for dense property-panel fields.
 *
 * The legacy implementation remains active until `fpl-migration` is enabled. In the FPL branch,
 * pass `labelFor` only when the row contains exactly one form control with that id; rows with
 * generated, multiple, or absent controls intentionally render their label as semantic text.
 */
export function PanelField({
  label,
  labelFor,
  controlGap,
  tooltipText,
  slot,
  useHorizontalLayout = false,
  showSlotOnHoverOnly = false,
  slotContainerH = 24,
  disabled = false,
  children,
  labelGap,
}: PanelFieldProps) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const implementationProps: PanelFieldImplementationProps = {
    children,
    controlGap,
    disabled,
    label,
    labelFor,
    labelGap,
    showSlotOnHoverOnly,
    slot,
    slotContainerH,
    tooltipText,
    useHorizontalLayout,
  };

  if (isFplMigrationEnabled) {
    return <FplPanelField {...implementationProps} />;
  }

  return <LegacyPanelField {...implementationProps} />;
}
